// See EnrollHostsDrawer.spec.md for EHD-FR-003
import { makeStyles } from '@material-ui/styles'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getBlueprints } from 'app/plugins/openstack/components/infrastructure/actions'
import withProgress from 'core/components/progress/withProgress'
import DropdownField from 'core/components/validatedForm/DropdownField'
import Badge from 'core/elements/badge'
import Card from 'core/elements/card'
import CardHeader from 'core/elements/card/CardHeader'
import Dropdown from 'core/elements/dropdown'
import Grid from 'core/elements/grid/Grid'
import type { GridCellProps } from 'core/elements/grid/hooks/useGridRows'
import Radio from 'core/elements/input/Radio'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import type Theme from 'core/themes/model'
import { listHostNetworkConfigs } from 'openstack/components/infrastructure/actions'
import {
  type HostNetworkConfig as BlueprintHostNetworkConfig,
  toInterfaceRows,
} from 'openstack/components/infrastructure/blueprint/helpers'
import { hostNetworkConfigsSelector } from 'openstack/components/infrastructure/selectors'
import type { EnrollStepProps } from './EnrollHostsDrawer'
import StepSection from './StepSection'

type InterfaceRow = ReturnType<typeof toInterfaceRows>[number]

interface HostNetworkConfig extends BlueprintHostNetworkConfig {
  id: string
  name?: string
  clusterName?: string
}

const AsyncDropdown = withProgress(Dropdown, { inline: true })

// EHD-FR-003: required dropdown with no 'None' option. Reuses listHostNetworkConfigs /
// hostNetworkConfigsSelector like HostNetworkConfigPicklist, but that picklist always adds a
// 'None' option so it isn't reused directly here.
function NetworkConfigPicklist({
  id,
  configs,
  loading,
  label,
  value,
  onChange,
}: {
  // Forwarded from DropdownField so the input gets its field test id (hostconfigid-dropdown-input)
  id?: string
  configs: HostNetworkConfig[]
  loading: boolean
  label?: string
  value?: string
  onChange: (value: string) => void
}) {
  const items = useMemo(
    () => configs.map((config) => ({ label: config.name || config.id, value: config.id })),
    [configs],
  )
  return (
    <AsyncDropdown
      id={id}
      name="hostConfigId"
      label={label}
      compact={false}
      loading={loading}
      // Dropdown shows "No ... available" for an empty list even mid load, so keep it
      // disabled (which hides that message) until the configs are in
      disabled={loading}
      items={items}
      value={value}
      onChange={onChange}
      placeholder="Select host configuration"
    />
  )
}

// EHD-FR-003: required field, no network config picked blocks Next
export const validateNetworkStep = (selection: { hostConfigId: string }): Record<string, string> =>
  selection.hostConfigId ? {} : { hostConfigId: 'Host configuration is required.' }

// Figma: a disabled radio per traffic type, filled when this interface carries it (same look as
// the blueprint page's interface table)
const BooleanCell = (label: string) =>
  function Cell({ item }: GridCellProps<InterfaceRow>) {
    const value = !!item[label as keyof InterfaceRow]
    return <Radio checked={value} disabled aria-label={`${label} ${value ? 'enabled' : 'off'}`} />
  }

export default function NetworkConfigStep({ selection, onChange, errors }: EnrollStepProps) {
  const classes = useStyles()
  const { loading: configsLoading } = useListAction(listHostNetworkConfigs)
  const data = useSelector(hostNetworkConfigsSelector) as HostNetworkConfig[]
  // null until the blueprint lookup answers, so the list isn't filtered by an empty name
  const [blueprintName, setBlueprintName] = useState<string | null>(null)
  const loading = configsLoading || blueprintName === null

  useEffect(() => {
    let active = true
    getBlueprints()
      .then((blueprints: Array<{ name?: string }>) => {
        if (active) setBlueprintName(blueprints?.[0]?.name || '')
      })
      .catch(() => {
        if (active) setBlueprintName('')
      })
    return () => {
      active = false
    }
  }, [])

  // Some configs are not tagged with a blueprint (clusterName); only scope by blueprint when
  // the data actually carries that field, same shape ClusterBlueprintPage filters on.
  const configs = useMemo(() => {
    const isBlueprintScoped = data.some((config) => !!config.clusterName)
    return isBlueprintScoped ? data.filter((config) => config.clusterName === blueprintName) : data
  }, [data, blueprintName])

  const selectedConfig = useMemo(
    () => configs.find((config) => config.id === selection.hostConfigId),
    [configs, selection.hostConfigId],
  )

  const interfaceRows = useMemo(
    () => (selectedConfig ? toInterfaceRows(selectedConfig) : []),
    [selectedConfig],
  )

  const handleChange = (id: string) => {
    const config = configs.find((item) => item.id === id)
    onChange({ hostConfigId: id, hostConfigName: config?.name || '' })
  }

  return (
    <div className={classes.root}>
      <StepSection
        title="Select Network Configuration"
        description="The selected host configuration applies to every host in this enrollment. The table below shows which traffic each interface carries. Host configurations are defined in the cluster blueprint and can't be edited here."
      >
        <div>
          {/* Required is enforced by validateNetworkStep; Figma shows no "*" on the label */}
          <DropdownField
            id="hostConfigId"
            label="Host Configuration"
            DropdownComponent={NetworkConfigPicklist}
            configs={configs}
            loading={loading}
            value={selection.hostConfigId}
            onChange={handleChange}
          />
          {errors?.hostConfigId && (
            <Text variant="caption1" className={classes.errorText}>
              {errors.hostConfigId}
            </Text>
          )}
        </div>
      </StepSection>
      <Card
        className={classes.tableCard}
        title={
          <CardHeader className={classes.tableHeader}>
            Interfaces ({interfaceRows.length})
            <Badge text="Read only" variant="default" />
          </CardHeader>
        }
      >
        {/* Same Card + Grid setup as step 6's tables (EnrollmentVerification) */}
        <Grid<InterfaceRow>
          uniqueIdentifier="physnet"
          data={interfaceRows}
          rowsPerPage={10}
          columns={[
            { key: 'physnet', label: 'Network Interface' },
            {
              key: 'physicalNetworkLabel',
              label: 'Physical Network Label',
              render: (value) => String(value ?? '') || '-',
            },
            { key: 'management', label: 'Management', CellComponent: BooleanCell('management') },
            { key: 'vmConsole', label: 'VM Console', CellComponent: BooleanCell('vmConsole') },
            {
              key: 'imageLibrary',
              label: 'Image Library I/O',
              CellComponent: BooleanCell('imageLibrary'),
            },
            { key: 'tunnel', label: 'Tunnel', CellComponent: BooleanCell('tunnel') },
            {
              key: 'hostLiveness',
              label: 'Host Liveness',
              CellComponent: BooleanCell('hostLiveness'),
            },
            {
              key: 'liveMigration',
              label: 'Live Migration',
              CellComponent: BooleanCell('liveMigration'),
            },
          ]}
          label="Interfaces"
          disableToolbar
          disableSorting
          hideCheckboxes
          useFloatingActionBar={false}
        />
      </Card>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
    display: 'block',
  },
  // Figma: the table sits flush in the card, so drop the card body padding and the table's own
  // border for this card only
  tableCard: {
    '& .card-body': {
      padding: 0,
    },
    '& .grid-container': {
      border: 0,
      borderRadius: 0,
    },
    // The grid's header row already draws a top line, so the card header's bottom line would
    // stack on it and look like a double border
    '& .card-header': {
      borderBottom: 0,
    },
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
}))
